import { useState, useRef, useMemo, useEffect } from "react";
import { isEmpty } from "@util/utils";

// 搜尋模式
const SearchMode = {
	FUZZY: "fuzzy", // 模糊搜尋 (預設)
	EXACT: "exact", // 精準搜尋
};

const useSearch = (sourceData, searchKeys = ["memberName"], searchModeProps = SearchMode.FUZZY) => {
	const [searchKeyword, setSearchKeyword] = useState("");
	const [searchMode, setSearchMode] = useState(searchModeProps);

	const isOnComposition = useRef(false);
	const timeoutRef = useRef(null);
	const inputRef = useRef(null);

	// **更新搜尋關鍵字（提供給外部按鈕用）**
	const updateSearchKeyword = value => {
		let keyword = "";
		if (value !== undefined) {
			keyword = String(value);
		} else if (inputRef.current) {
			// 如果 inputRef 引用的是標準 DOM 元素，會有 value 屬性。
			if (inputRef.current.value !== undefined) {
				keyword = inputRef.current.value;
			} else if (typeof inputRef.current.getResult === "function") {
				keyword = inputRef.current.getResult() || "";
			}
		}
		setSearchKeyword(keyword.trim());
	};

	// **過濾符合搜尋條件的資料**
	const filteredData = useMemo(
		() => {
			const trimmedKeyword = searchKeyword.trim();
			if (!trimmedKeyword || isEmpty(sourceData)) return sourceData;

			const keyword = trimmedKeyword.toLowerCase();

			return sourceData.filter(item =>
				searchKeys.some(key => {
					const itemValue = item[key].toString().toLowerCase();

					if (searchMode === SearchMode.EXACT) {
						return itemValue === keyword;
					} else {
						return itemValue.includes(keyword);
					}
				}),
			);
		},
		// eslint-disable-next-line
		[searchKeyword, sourceData, setSearchMode],
	);

	// **處理輸入變更（防抖）**
	const handleChange = e => {
		if (e.target instanceof HTMLInputElement && !isOnComposition.current) {
			clearTimeout(timeoutRef.current);
			const { value } = e.target;
			timeoutRef.current = setTimeout(() => {
				updateSearchKeyword(value);
			}, 300); // 防抖 300ms
		}
	};

	// **處理中文輸入法 Composition 事件**
	const handleComposition = e => {
		if (e.type === "compositionend") {
			isOnComposition.current = false;
			handleChange(e); // 中文輸入結束後再觸發搜尋
		} else {
			isOnComposition.current = true;
		}
	};

	// **清除 timeout，防止記憶體洩漏**
	useEffect(
		() => {
			return () => {
				clearTimeout(timeoutRef.current);
			};
		},
		// eslint-disable-next-line
		[],
	);

	return {
		searchKeyword,
		updateSearchKeyword,
		filteredData,
		inputRef,
		handleChange,
		handleComposition,
		searchMode,
		setSearchMode,
	};
};

export default useSearch;
